import React, { useState, useEffect, useCallback } from 'react';
import API from '../../api';
import { Building2, Save, AlertCircle, CheckCircle2, FileText } from 'lucide-react';

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{label}</label>
      {children}
    </div>
  );
}

export default function AziendaSdiTab() {
  const [data, setData] = useState(null);
  const [regimi, setRegimi] = useState({});
  const [modPag, setModPag] = useState({});
  const [condPag, setCondPag] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(() => {
    API.get('/api/sdi/azienda').then(r => {
      setData(r.data);
      setRegimi(r.data.regimi_fiscali || {});
    }).catch((err) => { console.error('Caricamento azienda SDI fallito:', err); });
    API.get('/api/sdi/azienda/defaults').then(r => {
      setModPag(r.data.modalita_pagamento_options || {});
      setCondPag(r.data.condizioni_pagamento_options || {});
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      const payload = {
        denominazione: data.denominazione, piva: data.piva, cf: data.cf,
        regime_fiscale: data.regime_fiscale,
        indirizzo: data.indirizzo, numero_civico: data.numero_civico,
        cap: data.cap, comune: data.comune, provincia: data.provincia, nazione: data.nazione,
        telefono: data.telefono, email: data.email, pec: data.pec, iban: data.iban,
        // REA (obbligatorio per società di capitali)
        rea_provincia: (data.rea_provincia || '').toUpperCase().slice(0, 2),
        rea_numero: (data.rea_numero || '').trim(),
        rea_capitale_sociale: parseFloat(data.rea_capitale_sociale || 0) || 0,
        rea_socio_unico: data.rea_socio_unico || '',
        rea_stato_liquidazione: data.rea_stato_liquidazione || 'LN',
        // pagamenti
        banca_nome: data.banca_nome || '',
        bic_swift: data.bic_swift || '',
        modalita_pagamento_default: data.modalita_pagamento_default || 'MP05',
        condizioni_pagamento_default: data.condizioni_pagamento_default || 'TP02',
        giorni_scadenza_default: parseInt(data.giorni_scadenza_default || 30),
        scadenza_fine_mese: !!data.scadenza_fine_mese,
        bollo_virtuale_automatico: data.bollo_virtuale_automatico !== false,
        note_pagamento_default: data.note_pagamento_default || '',
      };
      const r = await API.put('/api/sdi/azienda', payload);
      setData({ ...r.data, regimi_fiscali: regimi });
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch (e) { alert('Errore: ' + (e.response?.data?.detail || e.message)); }
    setSaving(false);
  };

  if (!data) return <div className="text-slate-400 py-8 text-center">Caricamento...</div>;

  const upd = (k, v) => setData({ ...data, [k]: v });
  const errors = data.errors || [];

  return (
    <div className="space-y-5" data-testid="azienda-sdi-tab">
      <div className={`rounded-md border p-4 flex items-start gap-3 ${errors.length === 0 ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
        {errors.length === 0 ? <CheckCircle2 size={20} className="text-green-600 flex-shrink-0 mt-0.5" /> : <AlertCircle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />}
        <div className="flex-1">
          <p className="font-bold text-sm">
            {errors.length === 0 ? 'Configurazione SDI completa' : `${errors.length} ${errors.length === 1 ? 'dato mancante' : 'dati mancanti'} per fatturazione elettronica`}
          </p>
          {errors.length > 0 && (
            <ul className="text-xs mt-1 text-yellow-800 list-disc pl-5">
              {errors.map((e) => <li key={e}>{e}</li>)}
            </ul>
          )}
          {data.progressivo_invio > 0 && (
            <p className="text-xs text-slate-500 mt-2">Ultimo progressivo invio: <b>{data.progressivo_invio}</b> (generato automaticamente ad ogni XML)</p>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4">
        <h3 className="font-black text-lg flex items-center gap-2">
          <Building2 size={18} className="text-primary" /> Anagrafica Cedente (EDILCIMA)
        </h3>

        <Field label="Denominazione / Ragione Sociale *">
          <input data-testid="azienda-denominazione" className="input w-full" value={data.denominazione || ''} onChange={e => upd('denominazione', e.target.value)} />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="P. IVA *">
            <input data-testid="azienda-piva" className="input w-full font-mono" value={data.piva || ''}
              onChange={e => upd('piva', e.target.value.replace(/\D/g, '').slice(0, 11))} />
          </Field>
          <Field label="Codice Fiscale">
            <input data-testid="azienda-cf" className="input w-full font-mono uppercase" value={data.cf || ''}
              onChange={e => upd('cf', e.target.value.toUpperCase().slice(0, 16))} />
          </Field>
        </div>

        <Field label="Regime Fiscale *">
          <select data-testid="azienda-regime" className="input w-full" value={data.regime_fiscale || 'RF01'} onChange={e => upd('regime_fiscale', e.target.value)}>
            {Object.entries(regimi).map(([k, v]) => (
              <option key={k} value={k}>{k} - {v}</option>
            ))}
          </select>
        </Field>

        <h4 className="text-xs uppercase tracking-wider text-slate-500 font-bold pt-2">Sede Legale</h4>
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-12 sm:col-span-8">
            <Field label="Indirizzo *">
              <input data-testid="azienda-indirizzo" className="input w-full" value={data.indirizzo || ''} onChange={e => upd('indirizzo', e.target.value)} />
            </Field>
          </div>
          <div className="col-span-6 sm:col-span-4">
            <Field label="Civico">
              <input className="input w-full" value={data.numero_civico || ''} onChange={e => upd('numero_civico', e.target.value)} />
            </Field>
          </div>
          <div className="col-span-6 sm:col-span-3">
            <Field label="CAP *">
              <input data-testid="azienda-cap" className="input w-full font-mono" maxLength={5} value={data.cap || ''}
                onChange={e => upd('cap', e.target.value.replace(/\D/g, '').slice(0, 5))} />
            </Field>
          </div>
          <div className="col-span-6 sm:col-span-6">
            <Field label="Comune *">
              <input data-testid="azienda-comune" className="input w-full" value={data.comune || ''} onChange={e => upd('comune', e.target.value)} />
            </Field>
          </div>
          <div className="col-span-6 sm:col-span-3">
            <Field label="Provincia *">
              <input data-testid="azienda-provincia" className="input w-full uppercase font-mono" maxLength={2} value={data.provincia || ''}
                onChange={e => upd('provincia', e.target.value.toUpperCase().slice(0, 2))} />
            </Field>
          </div>
        </div>

        <h4 className="text-xs uppercase tracking-wider text-slate-500 font-bold pt-2">
          Iscrizione REA (obbligatoria per SRL / SpA / Coop)
        </h4>
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-6 sm:col-span-3">
            <Field label="Provincia CCIAA *">
              <input data-testid="azienda-rea-provincia" className="input w-full uppercase font-mono" maxLength={2} placeholder="BS"
                value={data.rea_provincia || ''}
                onChange={e => upd('rea_provincia', e.target.value.toUpperCase().slice(0, 2))} />
            </Field>
          </div>
          <div className="col-span-6 sm:col-span-4">
            <Field label="Numero REA *">
              <input data-testid="azienda-rea-numero" className="input w-full font-mono" placeholder="es. 612345"
                value={data.rea_numero || ''}
                onChange={e => upd('rea_numero', e.target.value)} />
            </Field>
          </div>
          <div className="col-span-12 sm:col-span-5">
            <Field label="Capitale Sociale (€)">
              <input type="number" min={0} step="0.01" data-testid="azienda-rea-capitale" className="input w-full font-mono text-right"
                placeholder="10000.00" value={data.rea_capitale_sociale || ''}
                onChange={e => upd('rea_capitale_sociale', e.target.value)} />
            </Field>
          </div>
          <div className="col-span-6 sm:col-span-4">
            <Field label="Socio Unico">
              <select data-testid="azienda-rea-socio" className="input w-full" value={data.rea_socio_unico || ''}
                onChange={e => upd('rea_socio_unico', e.target.value)}>
                <option value="">— non specificato —</option>
                <option value="SU">SU - Socio Unico</option>
                <option value="SM">SM - Più Soci</option>
              </select>
            </Field>
          </div>
          <div className="col-span-6 sm:col-span-4">
            <Field label="Stato Liquidazione">
              <select data-testid="azienda-rea-liq" className="input w-full" value={data.rea_stato_liquidazione || 'LN'}
                onChange={e => upd('rea_stato_liquidazione', e.target.value)}>
                <option value="LN">LN - Non in liquidazione</option>
                <option value="LS">LS - In liquidazione</option>
              </select>
            </Field>
          </div>
          <div className="col-span-12 text-[11px] text-slate-500 -mt-2">
            💡 La <b>Provincia REA</b> è la sigla provincia dove la società è iscritta alla Camera di Commercio (es. BS per Brescia). Il <b>Numero REA</b> lo trovi nella visura camerale.
          </div>
        </div>

        <h4 className="text-xs uppercase tracking-wider text-slate-500 font-bold pt-2">Contatti</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Telefono"><input className="input w-full" value={data.telefono || ''} onChange={e => upd('telefono', e.target.value)} /></Field>
          <Field label="Email"><input type="email" className="input w-full" value={data.email || ''} onChange={e => upd('email', e.target.value)} /></Field>
          <Field label="PEC"><input type="email" className="input w-full" value={data.pec || ''} onChange={e => upd('pec', e.target.value)} /></Field>
          <Field label="IBAN"><input className="input w-full font-mono" value={data.iban || ''} onChange={e => upd('iban', e.target.value.toUpperCase().replace(/\s/g, ''))} /></Field>
        </div>

        <h4 className="text-xs uppercase tracking-wider text-slate-500 font-bold pt-2">Pagamenti & Scadenze (default fattura)</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Banca">
            <input data-testid="azienda-banca" className="input w-full" placeholder="Es. Intesa Sanpaolo - Filiale Roma" value={data.banca_nome || ''} onChange={e => upd('banca_nome', e.target.value)} />
          </Field>
          <Field label="BIC / SWIFT">
            <input data-testid="azienda-bic" className="input w-full font-mono uppercase" maxLength={11} value={data.bic_swift || ''} onChange={e => upd('bic_swift', e.target.value.toUpperCase().slice(0, 11))} />
          </Field>
          <Field label="Modalità Pagamento default">
            <select data-testid="azienda-mod-pag" className="input w-full" value={data.modalita_pagamento_default || 'MP05'} onChange={e => upd('modalita_pagamento_default', e.target.value)}>
              {Object.entries(modPag).map(([k, v]) => <option key={k} value={k}>{k} - {v}</option>)}
            </select>
          </Field>
          <Field label="Condizioni Pagamento default">
            <select data-testid="azienda-cond-pag" className="input w-full" value={data.condizioni_pagamento_default || 'TP02'} onChange={e => upd('condizioni_pagamento_default', e.target.value)}>
              {Object.entries(condPag).map(([k, v]) => <option key={k} value={k}>{k} - {v}</option>)}
            </select>
          </Field>
          <Field label="Giorni Scadenza default">
            <input type="number" data-testid="azienda-giorni-scad" className="input w-full" min={0} max={365} value={data.giorni_scadenza_default ?? 30} onChange={e => upd('giorni_scadenza_default', e.target.value)} />
          </Field>
          <Field label="Scadenza a fine mese">
            <label className="flex items-center gap-2 mt-2.5 cursor-pointer">
              <input type="checkbox" className="w-4 h-4" checked={!!data.scadenza_fine_mese} onChange={e => upd('scadenza_fine_mese', e.target.checked)} />
              <span className="text-sm">Sì, sposta a fine mese</span>
            </label>
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-3">
          <Field label="Note pagamento default (compaiono in fattura)">
            <textarea className="input w-full" rows={2} placeholder="Es. Bonifico a 30 giorni data fattura. Causale: Saldo fattura n. ..." value={data.note_pagamento_default || ''} onChange={e => upd('note_pagamento_default', e.target.value)} />
          </Field>
          <Field label="Bollo virtuale">
            <label className="flex items-center gap-2 mt-2.5 cursor-pointer">
              <input type="checkbox" className="w-4 h-4" checked={data.bollo_virtuale_automatico !== false} onChange={e => upd('bollo_virtuale_automatico', e.target.checked)} />
              <span className="text-sm">Aggiungi automaticamente €2 di bollo per fatture esenti IVA &gt; €77,47</span>
            </label>
          </Field>
        </div>

        <div className="flex items-center gap-3 pt-3 border-t border-slate-100">
          <button data-testid="save-azienda-btn" onClick={save} disabled={saving}
            className="px-5 py-2.5 bg-primary text-white font-bold rounded-md hover:bg-primary/90 transition-all flex items-center gap-2 disabled:opacity-50">
            <Save size={16} /> {saving ? 'Salvataggio...' : 'Salva Configurazione SDI'}
          </button>
          {saved && <span className="text-green-600 text-sm font-semibold flex items-center gap-1"><CheckCircle2 size={14} /> Salvato</span>}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-blue-900">
        <p className="font-bold mb-1 flex items-center gap-2"><FileText size={14} /> Come funziona</p>
        <ol className="list-decimal pl-5 space-y-1 text-xs">
          <li>Completa i dati dell'azienda sopra.</li>
          <li>Dall'anagrafica cliente aggiungi indirizzo, CAP, comune, provincia e Codice Destinatario SDI (o PEC).</li>
          <li>In Contabilita - Fatture, clicca l'icona "XML SDI" su ogni fattura per generare l'XML in formato FatturaPA v1.2.2.</li>
          <li>Scarica l'XML e trasmettilo tramite il tuo intermediario (es. FattureInCloud, Aruba, IX-FE, ecc.).</li>
        </ol>
      </div>
    </div>
  );
}
